import React from "react";
import { useTranslation } from "react-i18next";

import SiteNav from "components/Redesign/SiteNav.js";
import SiteFooter from "components/Redesign/SiteFooter.js";
import OccasionIcon from "components/Redesign/OccasionIcon.js";
import heroImg from "assets/img/hero/fuer-unternehmen-hero.jpg";
import giftImg from "assets/img/product-photos/honey-gift.jpg";
import whyFachausweisImg from "assets/img/unternehmen/why-fachausweis.jpg";
import whyLabelImg from "assets/img/unternehmen/why-label.jpg";
import whyRegionalImg from "assets/img/unternehmen/why-regional.jpg";

const OCCASIONS = [
  { icon: "gift", titleKey: "unternehmen.Occasion 1 title", bodyKey: "unternehmen.Occasion 1 body" },
  { icon: "heart", titleKey: "unternehmen.Occasion 2 title", bodyKey: "unternehmen.Occasion 2 body" },
  { icon: "star", titleKey: "unternehmen.Occasion 3 title", bodyKey: "unternehmen.Occasion 3 body" },
  { icon: "welcome", titleKey: "unternehmen.Occasion 4 title", bodyKey: "unternehmen.Occasion 4 body" },
];

const WHY_ITEMS = [
  {
    image: whyFachausweisImg,
    alt: "Eidgenössischer Fachausweis Imker, dekorativ präsentiert",
    titleKey: "unternehmen.Why 1 title",
    subheadingKey: "unternehmen.Why 1 subheading",
    bodyKey: "unternehmen.Why 1 body",
  },
  {
    image: whyLabelImg,
    alt: "Drei Honiggläser mit individuell gestalteten Firmenetiketten",
    titleKey: "unternehmen.Why 2 title",
    subheadingKey: "unternehmen.Why 2 subheading",
    bodyKey: "unternehmen.Why 2 body",
  },
  {
    image: whyRegionalImg,
    alt: "Nahaufnahme einer Bienenwabe mit Bienen",
    titleKey: "unternehmen.Why 3 title",
    subheadingKey: "unternehmen.Why 3 subheading",
    bodyKey: "unternehmen.Why 3 body",
  },
];

const STEPS = [
  { labelKey: "unternehmen.Step 1 label", titleKey: "unternehmen.Step 1 title", bodyKey: "unternehmen.Step 1 body" },
  { labelKey: "unternehmen.Step 2 label", titleKey: "unternehmen.Step 2 title", bodyKey: "unternehmen.Step 2 body" },
  { labelKey: "unternehmen.Step 3 label", titleKey: "unternehmen.Step 3 title", bodyKey: "unternehmen.Step 3 body" },
];

function FuerUnternehmen() {
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="rd">
      <SiteNav />

      <div className="hero-banner hero-banner-unternehmen">
        <img src={heroImg} alt="Wabe voller Bienen wird bei der Imkerei kontrolliert" className="hero-banner-img" />
      </div>

      <section className="page-shell">
        <div className="wrap">
          <div className="rd-section-no">{t("unternehmen.Page label")}</div>
          <h1 className="page-shell-h1">
            {t("unternehmen.Page title pre")}{" "}
            <em className="page-shell-h1-accent">{t("unternehmen.Page title accent")}</em>
          </h1>
          <div className="unternehmen-intro-split">
            <div className="unternehmen-intro">
              <p>{t("unternehmen.Intro p1")}</p>
              <p>{t("unternehmen.Intro p2")}</p>
            </div>
            <div className="unternehmen-intro-photo">
              <img src={giftImg} alt="Geschenkbox mit fünf Honiggläsern der Imkerei Bühler" />
            </div>
          </div>
        </div>
      </section>

      <section className="unternehmen-occasions-section">
        <div className="wrap">
          <h2 className="unternehmen-h2">{t("unternehmen.Occasions heading")}</h2>
          <div className="unternehmen-occasions">
            {OCCASIONS.map(item => (
              <div className="unternehmen-card" key={item.titleKey}>
                <OccasionIcon icon={item.icon} className="unternehmen-card-icon" />
                <h3>{t(item.titleKey)}</h3>
                <p>{t(item.bodyKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="unternehmen-why-section">
        <div className="wrap">
          <h2 className="unternehmen-h2">{t("unternehmen.Why heading")}</h2>
          <div className="unternehmen-why">
            {WHY_ITEMS.map(item => (
              <div className="unternehmen-card unternehmen-card-visual" key={item.titleKey}>
                <div className="unternehmen-card-image">
                  <img src={item.image} alt={item.alt} loading="lazy" />
                </div>
                <h3>{t(item.titleKey)}</h3>
                <div className="unternehmen-card-subheading">{t(item.subheadingKey)}</div>
                <p>{t(item.bodyKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="unternehmen-steps-section">
        <div className="wrap">
          <h2 className="unternehmen-h2">{t("unternehmen.Steps heading")}</h2>
          <div className="unternehmen-steps">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.titleKey}>
                <div className="unternehmen-step">
                  <div className="unternehmen-step-top">
                    <span className="unternehmen-step-num">{i + 1}</span>
                    <span className="unternehmen-step-label">{t(step.labelKey)}</span>
                  </div>
                  <h3>{t(step.titleKey)}</h3>
                  <p>{t(step.bodyKey)}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="unternehmen-step-arrow" aria-hidden="true">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="unternehmen-closing">
        <div className="wrap">
          <h2>{t("unternehmen.Closing heading")}</h2>
          <div className="unternehmen-closing-body">
            <p>{t("unternehmen.Closing p1")}</p>
            <p>{t("unternehmen.Closing p2")}</p>
            <p>{t("unternehmen.Closing p3")}</p>
          </div>
          <a className="cta-primary" href="mailto:ibm-honig@bluewin.ch?subject=Firmenanfrage">
            {t("unternehmen.Closing cta")} <span className="arr">→</span>
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default FuerUnternehmen;
